import { type Capability } from '@sisoma/auth';
import { isNavGroup, type NavLink, type NavSection } from '@sisoma/ui';

/**
 * Sidebar navigation registry — the single source of truth for what appears
 * in the sidenav.
 *
 * To add a link:
 *   1. Register a route (usually lazy) in `app.routes.ts`.
 *   2. Add an entry below with the same path.
 *   3. Need a new icon? Register it in the `@sisoma/ui` Icon component first
 *      (projects/ui/src/lib/icon/icon.ts).
 *
 * To add a collapsible group, give an entry `children` instead of a `route`.
 * The parent then expands in place when clicked rather than navigating, and
 * opens automatically when one of its children is the active route.
 *
 * ## Capability gating (fp1-04/fp1-05)
 *
 * An entry may carry an optional `capability` — the sidenav only shows it when
 * the active partner grants that capability — or an optional `anyCapability`
 * list, shown when the active partner grants **at least one** of them (GOV-07).
 * An entry carries **at most one** of the two; one with neither is ungated. An
 * empty `anyCapability` hides the entry: "any of nothing" is never held, and an
 * empty list is a mistake to surface, not a way to write "everyone".
 *
 * Gating lives here, not in `@sisoma/ui`, so the library's `NavLink`/`NavSection`
 * types stay purely presentational and auth-free. Run the registry through
 * {@link filterNav} before handing it to the sidenav (the `Shell` does this
 * reactively).
 *
 * `Members` (fp1-07), `Roles` (fp1-08), `Invites` (fp1-09), `Workspaces`
 * (fp1-10), `Billing` (fp3-01), `Audit Log` (PA-AUDIT) and the footer
 * `Settings` / `Single sign-on` are gated entries — each shows only when the
 * active partner grants the matching capability (`partner:manage_members` /
 * `partner:manage_roles` / `partner:invite` / `partner:manage_workspaces` /
 * `partner:manage_billing` / `partner:view_audit` / `partner:manage_settings` /
 * `partner:manage_sso`).
 *
 * The `Content` group (PA-CONTENT) gates per child rather than on the group:
 * `Categories` needs `content:manage_categories`, `Content` needs
 * `content:view`. `filterNav` drops the whole group when neither is held, so a
 * capability-less member sees nothing and an Editor without
 * `content:manage_categories` sees only the `Content` child.
 *
 * `Compliance` (PA-COMPL) is a group, gated per child since wave E — the same
 * pattern as Content. The read surfaces (Dashboard, Bot) gate on
 * `compliance:view`: write authority (`compliance:manage_bots`) is *not* their
 * nav gate, because a view-only member still needs to reach them to read what
 * the firm's bot is doing, and those screens hide their own write controls.
 * `filterNav` keeps the group as long as any child survives.
 *
 * Six children are exceptions to that read gate:
 *
 * `Triage Rules` (GOV-03) gates on **`compliance:manage_policies`**. The API
 * moved every triage route there, the config read included (O3/U28): under
 * approvals a draft rule set is governed content, and "a triage rule is a
 * rule". A `view` holder would reach a screen whose first read 403s.
 *
 * `Approvals` (wave D) gates on **`compliance:approve`**. It is not a record to
 * be read — it is a worklist of decisions, and the only people who can act on
 * any row in it are the ones holding `compliance:approve`. Showing it to a
 * view-only member would be a queue of buttons they cannot press. This is also
 * the client-side gate the legacy creator screen never had: there, the queue was
 * reachable by anyone who could see compliance and the server was the only thing
 * saying no.
 *
 * `Access` (wave E) gates on **`compliance:manage_access`** — it lists client
 * firms and individuals by name and email, which has no view-only audience.
 *
 * `Reports` (wave F) gates on **`compliance:reports`** — the reports seat
 * (plan §6.2). Deliberately not `compliance:view`, which the Editor bundle
 * holds: an author seeing posture for their own posts is a different audience
 * from an officer reading league tables across the firm.
 *
 * `Rule changes` (GOV-07) gates on **`compliance:manage_policies` or
 * `compliance:manage_sections`** — the registry's first any-of entry. Those are
 * the two capabilities that govern a rule change, and either is enough to have
 * decisions waiting on the screen (see the note on the entry).
 *
 * `Test` (GOV-09) carries the same any-of pair. A test run varies the rule set
 * and the pack (`manage_policies`) or the sections (`manage_sections`), and
 * either is enough to run one; the screen locks the dimensions the viewer does
 * not govern to Live. Never `compliance:view` (O14): a run spends the firm's
 * test posts and reads its drafts.
 */

/**
 * The gate an entry may carry: **at most one** of `capability` (that one is
 * required) or `anyCapability` (at least one of these is required). Neither
 * means ungated. An empty `anyCapability` hides the entry.
 */
export interface NavGate {
  capability?: Capability;
  anyCapability?: readonly Capability[];
}

/** A `NavLink` that may be hidden unless the active partner passes its {@link NavGate}. */
export interface GatedNavLink extends NavLink, NavGate {}

/** A collapsible group that may itself be gated, with gated child links. */
export interface GatedNavGroup extends NavGate {
  id?: string;
  label: string;
  icon: NavLink['icon'];
  children: GatedNavLink[];
}

export type GatedNavItem = GatedNavLink | GatedNavGroup;

/** A titled section that may itself be gated, holding gated entries. */
export interface GatedNavSection extends NavGate {
  heading?: string;
  items: GatedNavItem[];
}

export const NAV_SECTIONS: GatedNavSection[] = [
  {
    items: [
      { label: 'Dashboard', icon: 'dashboard', route: 'dashboard' },
      { label: 'Members', icon: 'users', route: 'members', capability: 'partner:manage_members' },
      { label: 'Roles', icon: 'shield', route: 'roles', capability: 'partner:manage_roles' },
      { label: 'Invites', icon: 'user-plus', route: 'invites', capability: 'partner:invite' },
      {
        label: 'Workspaces',
        icon: 'briefcase',
        route: 'workspaces',
        capability: 'partner:manage_workspaces',
      },
      {
        label: 'Billing',
        icon: 'credit-card',
        route: 'billing',
        capability: 'partner:manage_billing',
      },
      // Audit Log (PA-AUDIT §6). A settings sub-page — the design puts it in the
      // Settings sub-nav, which does not exist yet, so it links from here until it
      // does. `partner:view_audit` is Owner/Manager-only (decision §9.1).
      {
        label: 'Audit Log',
        icon: 'file-text',
        route: 'settings/audit',
        capability: 'partner:view_audit',
      },
    ],
  },
  {
    heading: 'Library',
    items: [
      {
        // Explicit `id` because the group and its second child share the label
        // "Content" — `navItemKey` falls back to a group's label, and relying on
        // display text for identity breaks on a rename.
        id: 'content',
        label: 'Content',
        icon: 'layers',
        children: [
          {
            label: 'Categories',
            icon: 'tag',
            route: 'content/categories',
            capability: 'content:manage_categories',
          },
          {
            label: 'Content',
            icon: 'content',
            route: 'content/library',
            capability: 'content:view',
          },
        ],
      },
      // Compliance (PA-COMPL). A group from wave B, when the Bot and Triage
      // Rules screens land beside the dashboard. The label, the icon and the
      // `compliance:view` gate are the same ones wave A shipped, so the entry
      // does not move under anyone who had already found it.
      //
      // `check-square` rather than `shield`: Roles already owns `shield`, and two
      // entries drawn with the same glyph are indistinguishable at a glance,
      // which is the one job a sidebar icon has.
      //
      // Explicit `id` because the group and its first child would otherwise be
      // told apart by display text alone — `navItemKey` falls back to a group's
      // label, and relying on that breaks on a rename.
      //
      // There is no `Bots` plural anywhere here, and there must not be: a partner
      // has exactly one bot (plan §6), so the child is `Bot` and every screen
      // under it addresses "the bot". Documents and Sections are platform-managed
      // and live in the admin app — they are absent rather than disabled, because
      // a greyed-out tab is a promise that something will appear there.
      {
        id: 'compliance',
        label: 'Compliance',
        icon: 'check-square',
        children: [
          {
            label: 'Dashboard',
            icon: 'bar-chart-2',
            route: 'compliance',
            capability: 'compliance:view',
          },
          // Approvals sits second: it is the only child anyone visits daily, and
          // it is a worklist rather than a record — hence `compliance:approve`
          // and not `compliance:view` (see the note above). `user-check` rather
          // than `check-circle`: the group's own glyph is already `check-square`
          // and three check marks in one expanded group are one glyph three
          // times over.
          {
            label: 'Approvals',
            icon: 'user-check',
            route: 'compliance/approvals',
            capability: 'compliance:approve',
          },
          // Reports (wave F) gates on **`compliance:reports`**, not
          // `compliance:view` — the third child, and with Approvals and Access
          // one of the three gated more tightly than the group. `compliance:view` is on the
          // Editor bundle so an author can see posture for their own posts;
          // Reports is an officer's surface over everyone's (cross-user league
          // tables, the workspace roster with resolved names, caption text), and
          // giving it its own seat is what let wave F drop the per-dataset gates
          // its earlier drafts composed (plan §6.2).
          //
          // `table` rather than a chart glyph: the Dashboard child directly above
          // is already `bar-chart-2`, and two chart silhouettes in one expanded
          // group are the same "one glyph twice" problem the Approvals note
          // describes. `pie-chart` and `bar-chart` are rejected for that reason,
          // `file-text` because Audit Log owns it.
          {
            label: 'Reports',
            icon: 'table',
            route: 'compliance/reports',
            capability: 'compliance:reports',
          },
          {
            label: 'Bot',
            icon: 'check-square',
            route: 'compliance/bot',
            capability: 'compliance:view',
          },
          {
            label: 'Triage Rules',
            icon: 'filter',
            route: 'compliance/bot/triage',
            capability: 'compliance:manage_policies',
          },
          // Policy packs (GOV-04) — the severity levels, scoring, warning
          // triggers and placement a check scores against. After Triage Rules and
          // before Test, in the order the work goes: draft, test, approve. Every
          // pack route in the API needs `manage_policies`, reads included, so
          // the entry does too. `book-open`: a rulebook, the glyph the admin
          // app's packs entry uses, and no other entry in this group draws it.
          {
            label: 'Policy packs',
            icon: 'book-open',
            route: 'compliance/policy-packs',
            capability: 'compliance:manage_policies',
          },
          // Test (GOV-09) — try a post against the live rules or a draft. It sits
          // between the rules and their approval queue, in the order the work
          // goes: draft, test, approve. Gated like Rule changes below, on either
          // governing capability, and never `compliance:view` (see the note
          // above). The static route stays lit on `compliance/test/runs/...`,
          // because the sidenav matches child links by prefix.
          //
          // `play-circle`: a run, and no other entry draws it.
          {
            label: 'Test',
            icon: 'play-circle',
            route: 'compliance/test',
            anyCapability: ['compliance:manage_policies', 'compliance:manage_sections'],
          },
          // Rule changes (GOV-07) — the approval queue for changes to the rules
          // themselves, after the rules it governs and the Test screen. The
          // registry's first any-of entry (ruling T6): `manage_policies` governs
          // triage and policy packs, `manage_sections` governs sections, and
          // either is a reason to be here. Not an all-of, and not `manage_sections` alone:
          // sections are hidden at launch (D19, `LAUNCH_HIDDEN_CAPABILITIES`),
          // so at launch `manage_policies` on its own must reveal the entry.
          // Not `compliance:view` either — like Approvals, it is a worklist of
          // decisions, and a reader who governs nothing could decide none of it.
          //
          // `git-pull-request`: a proposed change waiting on somebody else's
          // review is exactly what the glyph draws, and no other entry uses it.
          // `filter` (Triage Rules), `play-circle` (Test) and `user-check`
          // (Approvals) are the near neighbours it must not echo.
          {
            label: 'Rule changes',
            icon: 'git-pull-request',
            route: 'compliance/governance',
            anyCapability: ['compliance:manage_policies', 'compliance:manage_sections'],
          },
          // Gated per child, like the Content group — NOT `compliance:view`. The
          // Access screen enumerates client firms and individuals by name and
          // email, which is manage_access data with no view-only audience; a
          // member with only `compliance:view` keeps the rest of the group and
          // never sees this entry (wave E, plan §6).
          {
            label: 'Access',
            icon: 'users',
            route: 'compliance/access',
            capability: 'compliance:manage_access',
          },
        ],
      },
    ],
  },
];

/** Links pinned to the bottom of the sidenav. Groups are not supported here. */
export const FOOTER_NAV_ITEMS: GatedNavLink[] = [
  { label: 'Settings', icon: 'settings', route: 'settings', capability: 'partner:manage_settings' },
  // A settings sub-page, but linked directly: `partner:manage_sso` can be held
  // on its own by an IT delegate who has no `partner:manage_settings` and so
  // never sees the Settings entry above it.
  {
    label: 'Single sign-on',
    icon: 'key',
    route: 'settings/sso',
    capability: 'partner:manage_sso',
  },
];

/**
 * Drop every entry the active partner cannot see, and return the plain
 * presentational `NavSection[]` the sidenav consumes.
 *
 * Pure — `has` is the only source of reactivity. An ungated entry is always
 * kept (see {@link passesGate}); a group is kept only if it passes its own gate
 * and still has at least one visible child; a section that empties out is
 * dropped.
 */
export function filterNav(
  sections: readonly GatedNavSection[],
  has: (capability: Capability) => boolean,
): NavSection[] {
  const allowed = (item: NavGate): boolean => passesGate(item, has);

  return sections
    .filter(allowed)
    .map((section) => ({
      heading: section.heading,
      items: section.items.filter(allowed).flatMap((item): GatedNavItem[] => {
        if (!isGatedGroup(item)) {
          return [item];
        }
        const children = item.children.filter(allowed);
        return children.length > 0 ? [{ ...item, children }] : [];
      }),
    }))
    .filter((section) => section.items.length > 0);
}

/** Filter the footer links the same way (flat — no groups). */
export function filterNavLinks(
  links: readonly GatedNavLink[],
  has: (capability: Capability) => boolean,
): NavLink[] {
  return links.filter((link) => passesGate(link, has));
}

/**
 * Whether an entry's gate lets the viewer see it.
 *
 * `anyCapability` is read by presence, not by length: an empty list asks for
 * "at least one of nothing", which nobody holds, so the entry is hidden rather
 * than silently opened to everyone. An entry is meant to carry only one of the
 * two keys; if it ever carried both, both would have to pass.
 */
function passesGate(gate: NavGate, has: (capability: Capability) => boolean): boolean {
  const single = gate.capability === undefined || has(gate.capability);
  const anyOf =
    gate.anyCapability === undefined || gate.anyCapability.some((capability) => has(capability));
  return single && anyOf;
}

/** True when a gated entry expands rather than navigates. Mirrors ui `isNavGroup`. */
function isGatedGroup(item: GatedNavItem): item is GatedNavGroup {
  return isNavGroup(item);
}
